export const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000'

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

import { fetchAuthSession } from 'aws-amplify/auth'

async function getValidToken(): Promise<string | null> {
  const current = localStorage.getItem('authToken')
  try {
    const session = await fetchAuthSession()
    const fresh = session.tokens?.accessToken?.toString()
    if (fresh && fresh !== current) localStorage.setItem('authToken', fresh)
    return fresh || current
  } catch {
    return current
  }
}

export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getValidToken()
  
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  }

  const url = `${API_BASE_URL}${endpoint}`

  const attempt = async (headers: Record<string, string>) => fetch(url, { ...options, headers })

  const response = await attempt(config.headers as Record<string, string>)

  if (response.status === 401) {
    // Try once more with a refreshed token
    const refreshed = await getValidToken()
    const retryHeaders = {
      'Content-Type': 'application/json',
      ...(refreshed && { Authorization: `Bearer ${refreshed}` }),
      ...options.headers,
    } as Record<string, string>
    const retryRes = await attempt(retryHeaders)
    if (retryRes.ok) {
      return await retryRes.json()
    }
    const retryErr = await retryRes.json().catch(() => ({}))
    throw new ApiError(retryErr.message || retryErr.detail || `HTTP ${retryRes.status}`, retryRes.status, retryErr)
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.message || errorData.detail || `HTTP ${response.status}`,
      response.status,
      errorData
    )
  }

  return await response.json()
}

export const api = {
  get: <T = any>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { method: 'GET', ...options }),

  post: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    }),

  put: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    }),

  delete: <T = any>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { method: 'DELETE', ...options }),
}

export { ApiError } 